import { useEffect, useRef, useState } from "react";

import usePrefTickets from "../usePrefTickets";

// Uma borda que se arrasta para mudar a largura de um bloco, com o valor
// lembrado no usuário. São três na tela de atendimentos (a lista, a conversa
// no Foco e o painel do contato) e todas têm a mesma parte chata: guardar o
// tamanho durante o arrasto sem re-renderizar a cada pixel.
//
// `invertido` é para a borda que fica à ESQUERDA do bloco: ali, puxar o mouse
// para a direita encolhe. Sem isso o arrasto anda ao contrário da mão.
//
// `aPartirDe` é o ponto em que a largura passa a valer: abaixo dele o bloco
// costuma ser "tela inteira" por classe, e estilo inline ganharia dela —
// levando a largura do computador para o celular.
const useLarguraArrastavel = ({
	chave,
	padrao,
	min,
	max,
	invertido = false,
	aPartirDe = 1024,
}) => {
	const ref = useRef(null);
	const [largura, setLargura] = usePrefTickets(chave, padrao);

	const consulta = `(min-width: ${aPartirDe}px)`;
	const [ativo, setAtivo] = useState(
		() => window.matchMedia(consulta).matches
	);

	useEffect(() => {
		const mq = window.matchMedia(consulta);
		const atualizar = () => setAtivo(mq.matches);

		mq.addEventListener("change", atualizar);
		return () => mq.removeEventListener("change", atualizar);
	}, [consulta]);

	const iniciarArrasto = (e) => {
		e.preventDefault();

		const inicioX = e.clientX;
		const larguraInicial = ref.current.getBoundingClientRect().width;
		const maximo = typeof max === "function" ? max() : max;
		const sentido = invertido ? -1 : 1;

		// O bloco pode ter transição de largura (o painel do contato tem, para
		// abrir e fechar suave). Durante o arrasto ela atrapalha duas vezes: a
		// borda anda atrasada em relação à mão, e o tamanho medido no fim ainda
		// é o antigo. Some enquanto se arrasta e volta ao soltar.
		const transicaoOriginal = ref.current.style.transition;
		ref.current.style.transition = "none";

		// Durante o arrasto a largura vai direto no DOM: um setState por
		// pointermove re-renderizaria o bloco dezenas de vezes por segundo. O
		// estado (e a gravação) só acontecem quando o ponteiro solta — e com o
		// valor CALCULADO, não o medido: medir depende do reflow ter acontecido.
		let ultima = larguraInicial;

		const mover = (ev) => {
			ultima = Math.min(
				maximo,
				Math.max(min, larguraInicial + sentido * (ev.clientX - inicioX))
			);

			ref.current.style.width = `${ultima}px`;
		};

		const soltar = () => {
			window.removeEventListener("pointermove", mover);
			window.removeEventListener("pointerup", soltar);
			ref.current.style.transition = transicaoOriginal;
			setLargura(Math.round(ultima));
		};

		window.addEventListener("pointermove", mover);
		window.addEventListener("pointerup", soltar);
	};

	return { ref, largura, ativo, iniciarArrasto };
};

export default useLarguraArrastavel;
